/**
 * Shared validation utilities for form fields.
 */

/**
 * Validates a GS1-13 / EAN-13 barcode.
 * The check digit is computed by multiplying each digit at an even index (0-based)
 * by 1 and each digit at an odd index by 3, summing all products, and verifying
 * that the total is divisible by 10.
 *
 * EAN validation only applies to Danish customers (countryCode === "DK").
 * For all other countries the function returns undefined (i.e. no error).
 *
 * @param countryCode ISO 3166-1 alpha-2 country code. Defaults to "DK" for
 *   backwards-compatibility with callers that do not pass a country.
 * @returns undefined if valid (or skipped), an error string if invalid.
 */
export function validateEAN(value: string, countryCode = "DK"): string | undefined {
  if (countryCode !== "DK") {
    return undefined;
  }

  if (!/^\d{13}$/.test(value)) {
    return "EAN must be exactly 13 digits";
  }

  // GS1-13 spec: weights alternate ×1 (even index) / ×3 (odd index), sum mod 10 === 0
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    const digit = parseInt(value[i], 10);
    sum += i % 2 === 0 ? digit : digit * 3;
  }

  if (sum % 10 !== 0) {
    return "Invalid EAN Number (wrong check digit)";
  }

  return undefined;
}

/**
 * Validates a Danish CVR number using modulo-11 weighted checksum.
 * Weights: [2, 7, 6, 5, 4, 3, 2, 1].
 * Sum of (digit * weight) must be divisible by 11.
 *
 * CVR validation only applies to Danish customers (countryCode === "DK").
 * For all other countries the function returns undefined (i.e. no error).
 *
 * @param countryCode ISO 3166-1 alpha-2 country code. Defaults to "DK" for
 *   backwards-compatibility with callers that do not pass a country.
 * @returns undefined if valid (or skipped), an error string if invalid.
 */
export function validateCVR(value: string | number | null | undefined, countryCode = "DK"): string | undefined {
  if (countryCode !== "DK") {
    return undefined;
  }

  const str = String(value ?? "");
  if (!/^\d{8}$/.test(str)) {
    return "Invalid CVR";
  }

  const weights = [2, 7, 6, 5, 4, 3, 2, 1];
  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += parseInt(str[i], 10) * weights[i];
  }

  if (sum % 11 !== 0) {
    return "Invalid CVR";
  }

  return undefined;
}

/**
 * Validates a postal code. Danish postal codes must be exactly 4 digits; for
 * all other countries only a loose sanity check is applied so international
 * formats (e.g. "NL 1234 AB", "PL 00-001", "IE D02 AF30") are not rejected.
 *
 * @returns undefined if valid (or empty), an error string if invalid.
 */
export function validatePostalCode(
  value: string | null | undefined,
  countryCode = "DK"
): string | undefined {
  const str = String(value ?? "").trim();
  if (!str) return undefined;

  if (countryCode === "DK") {
    return /^\d{4}$/.test(str)
      ? undefined
      : "Danish postal code must be 4 digits";
  }

  // Deliberately permissive — postal formats vary widely (spaces, dashes,
  // slashes and dots all occur), so this only rejects obvious junk. Capped
  // at 12 characters, which comfortably covers real-world EU formats.
  return /^[A-Za-z0-9][A-Za-z0-9 \-/.]{0,11}$/.test(str)
    ? undefined
    : "Postal code must be 1-12 letters, numbers, spaces or dashes";
}

/**
 * Validates a phone number. Accepts an optional leading "+" followed by
 * digits, spaces, dashes and parentheses, and requires at least 8 digits.
 *
 * @returns undefined if valid (or empty), an error string if invalid.
 */
export function validatePhone(
  value: string | null | undefined
): string | undefined {
  const str = String(value ?? "").trim();
  if (!str) return undefined;

  if (!/^\+?[\d\s()-]+$/.test(str)) {
    return "Phone number may only contain digits, spaces, +, - and ()";
  }

  const digits = str.replace(/\D/g, "");
  if (digits.length < 8) return "Phone number must contain at least 8 digits";
  if (digits.length > 15) return "Phone number is too long";

  return undefined;
}
